import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCompetencesSpecifiquesStepRequest, saveAllCompetencesSpecifiquesAutoEvalRequest } from "../../../../redux/actions/performanceActions";
import { asArray, StepState } from "../performanceUi";
import "./CompetencesSpecifiquesMatrix.css";

const PAGE_SIZE = 10;

const buildFallbackLevels = (evaluations) => {
    const max = Math.max(1, ...evaluations.map((evaluation) => Number(evaluation.niveauMax) || 0));
    return Array.from({ length: max }, (_, index) => ({
        niveau: index + 1,
        nom: `Niveau ${index + 1}`,
    }));
};

export default function CompetencesSpecifiquesStep({ progressId }) {
    const dispatch = useDispatch();
    const slice = useSelector((state) => state.performance.competencesSpecifiques);
    const data = slice.stepByProgressId[String(progressId)];
    const loading = slice.loadingByProgressId[String(progressId)];
    const error = slice.errorByProgressId[String(progressId)];
    const evaluations = asArray(data?.evaluations);
    const levels = asArray(data?.niveaux).length > 0 ? asArray(data?.niveaux) : buildFallbackLevels(evaluations);
    const [drafts, setDrafts] = useState({});
    const [page, setPage] = useState(1);
    const [details, setDetails] = useState(null);

    useEffect(() => {
        dispatch(fetchCompetencesSpecifiquesStepRequest(progressId));
    }, [dispatch, progressId]);

    const managerValidated = data?.managerEvaluationCompleted || data?.stepStatus === "COMPLETED";
    const hasAutoEvaluation = evaluations.some((evaluation) => evaluation.niveauAutoEvalue != null);
    const statusLabel = managerValidated ? "Validee par manager" : hasAutoEvaluation ? "En attente validation manager" : "A saisir";
    const pageCount = Math.max(1, Math.ceil(evaluations.length / PAGE_SIZE));
    const visibleEvaluations = useMemo(() => evaluations.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [evaluations, page]);

    const updateDraft = (id, patch) => setDrafts((current) => ({ ...current, [id]: { ...(current[id] || {}), ...patch } }));

    const saveAll = () => {
        if (managerValidated) return;
        const items = evaluations
            .filter((evaluation) => evaluation.niveauManager == null)
            .map((evaluation) => {
                const draft = drafts[evaluation.id] || {};
                const commentaire = draft.commentaireAutoEvaluation ?? evaluation.commentaireAutoEvaluation ?? "";
                return {
                    evaluationProgressId: Number(progressId),
                    competenceSpecifiqueEvaluationId: evaluation.id,
                    niveauAutoEvalue: draft.niveauAutoEvalue ?? evaluation.niveauAutoEvalue,
                    commentaire: commentaire.trim(),
                };
            });
        if (items.some((item) => !item.niveauAutoEvalue)) {
            window.alert("Veuillez choisir un niveau pour chaque competence.");
            return;
        }
        dispatch(saveAllCompetencesSpecifiquesAutoEvalRequest(Number(progressId), items));
    };

    return (
        <StepState loading={loading} error={error} empty={!data || evaluations.length === 0}>
            {data?.blockedReason && <div className="perf-state perf-state--warning">{data.blockedReason}</div>}
            <div className={`perf-state perf-state--inline ${managerValidated ? "" : "perf-state--warning"}`}>{statusLabel}</div>
            <div className="perf-actions perf-actions--top">
                <button className="perf-btn perf-btn--primary" type="button" disabled={managerValidated || evaluations.length === 0} onClick={saveAll}>
                    {managerValidated ? "Validation manager effectuee" : "Enregistrer toutes les compétences"}
                </button>
            </div>

            <section className="cs-matrix" style={{ "--cs-level-count": levels.length }}>
                <header className="cs-matrix__header">
                    <strong>Competence ({evaluations.length} au total)</strong>
                    {levels.map((level) => (
                        <div className="cs-matrix__level-head" key={level.id || level.niveau}>
                            <span>{level.nom}</span>
                            <i />
                        </div>
                    ))}
                    <span />
                </header>

                {visibleEvaluations.map((evaluation) => {
                    const draft = drafts[evaluation.id] || {};
                    const selected = Number(draft.niveauAutoEvalue ?? evaluation.niveauAutoEvalue);
                    const locked = managerValidated || evaluation.niveauManager != null;
                    return (
                        <article className={`cs-matrix__row${locked ? " cs-matrix__row--locked" : ""}`} key={evaluation.id}>
                            <div className="cs-matrix__skill">
                                <span>{evaluation.skillNom || evaluation.skillCode}</span>
                                <button type="button" onClick={() => setDetails(evaluation)}>Details</button>
                            </div>
                            {levels.map((level) => (
                                <label className="cs-matrix__radio" key={level.id || level.niveau}>
                                    <input
                                        type="radio"
                                        name={`auto-${evaluation.id}`}
                                        disabled={locked}
                                        checked={selected === Number(level.niveau)}
                                        onChange={() => updateDraft(evaluation.id, { niveauAutoEvalue: level.niveau })}
                                    />
                                    <span />
                                </label>
                            ))}
                            {/*<span className="cs-matrix__save">{locked ? "Verrouille" : ""}</span>*/}
                        </article>
                    );
                })}

                <footer className="cs-matrix__pagination">
                    <button type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>&lt;</button>
                    {Array.from({ length: pageCount }, (_, index) => (
                        <button key={index + 1} type="button" className={page === index + 1 ? "active" : ""} onClick={() => setPage(index + 1)}>{index + 1}</button>
                    ))}
                    <button type="button" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}>&gt;</button>
                    <span>Page {page} sur {pageCount} | Competences {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, evaluations.length)} sur {evaluations.length}</span>
                </footer>
            </section>

            {details && (
                <div className="cs-modal-backdrop" role="presentation" onMouseDown={() => setDetails(null)}>
                    <section className="cs-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
                        <header>
                            <h3>{details.skillNom || details.skillCode}</h3>
                            <button type="button" onClick={() => setDetails(null)}>x</button>
                        </header>
                        <p>{details.jobName || details.skillType || "Competence specifique"}</p>
                        <dl className="perf-facts">
                            <div><dt>Niveau attendu</dt><dd>{details.niveauAttenduNom || details.niveauAttendu || "-"}</dd></div>
                            <div><dt>Validation manager</dt><dd>{details.niveauManager != null ? `Niveau ${details.niveauManager}` : "-"}</dd></div>
                            <div><dt>Commentaire manager</dt><dd>{details.commentaireManagerEvaluation || "-"}</dd></div>
                        </dl>
                        <label className="perf-field">
                            <span>Commentaire collaborateur</span>
                            <textarea
                                disabled={managerValidated || details.niveauManager != null}
                                value={drafts[details.id]?.commentaireAutoEvaluation ?? details.commentaireAutoEvaluation ?? ""}
                                onChange={(event) => updateDraft(details.id, { commentaireAutoEvaluation: event.target.value })}
                            />
                        </label>
                        <footer>
                            <button className="perf-btn" type="button" onClick={() => setDetails(null)}>Fermer</button>
                        </footer>
                    </section>
                </div>
            )}
        </StepState>
    );
}
